import { Trans, useTranslation } from "react-i18next";
import { Icon } from "components/icon/Icon";
import { Button } from "components/button";
import { Text } from "components/text";
import {
  SalesDataExplainerCard,
  SalesDataExplainerCardProps,
} from "./SalesDataExplainerCard";

type ExplainerMetric = SalesDataExplainerCardProps & { id: string };

export const SalesDataExplainer = ({
  onDismiss,
}: {
  onDismiss: () => void;
}) => {
  const { t } = useTranslation(["productDetails"]);

  const metrics: ExplainerMetric[] = [
    {
      id: "units",
      icon: "package",
      iconBg: "bg-primary-50",
      iconColor: "text-primary-600",
      label: t("productDetails:salesData.explainer.metrics.units.label"),
      desc: t("productDetails:salesData.explainer.metrics.units.desc"),
      source: "mediaControl",
    },
    {
      id: "sales",
      icon: "banknote",
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
      label: t("productDetails:salesData.explainer.metrics.sales.label"),
      desc: t("productDetails:salesData.explainer.metrics.sales.desc"),
      source: "mediaControl",
    },
    {
      id: "conversion",
      icon: "percent",
      iconBg: "bg-primary-50",
      iconColor: "text-primary-600",
      label: t("productDetails:salesData.explainer.metrics.conversion.label"),
      desc: t("productDetails:salesData.explainer.metrics.conversion.desc"),
    },
    {
      id: "ranking",
      icon: "trophy",
      iconBg: "bg-amber-50",
      iconColor: "text-amber-600",
      label: t("productDetails:salesData.explainer.metrics.ranking.label"),
      desc: t("productDetails:salesData.explainer.metrics.ranking.desc"),
    },
  ];

  return (
    <div
      data-testid="sales-data-explainer"
      className="relative rounded-[14px] border border-primary-200 bg-gradient-to-br from-primary-25 to-white px-6 pt-5 pb-[22px] shadow-sm"
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t("productDetails:salesData.explainer.dismissAria")}
        data-testid="sales-data-explainer-dismiss"
        className="absolute top-3 right-3 rounded-md p-1.5 text-secondary-700 transition-colors hover:bg-black/5"
      >
        <Icon name="x" size="small" />
      </button>

      <div className="flex items-center gap-3">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-50">
          <Icon name="info" size="small" iconClassName="text-green-700" />
        </span>
        <div>
          <Text as="h3" variant="heading">
            {t("productDetails:salesData.explainer.title")}
          </Text>
          <Text variant="muted" className="mt-0.5">
            {t("productDetails:salesData.explainer.subtitle")}
          </Text>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-3.5">
        {metrics.map((metric) => (
          <SalesDataExplainerCard
            key={metric.id}
            icon={metric.icon}
            iconBg={metric.iconBg}
            iconColor={metric.iconColor}
            label={metric.label}
            desc={metric.desc}
            source={metric.source}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-primary-100 pt-3">
        <Text as="span" variant="caption" className="max-w-[560px]">
          <Trans
            i18nKey="productDetails:salesData.explainer.footerSource"
            components={{
              strong: <strong className="font-semibold text-secondary-700" />,
            }}
          />
          <br />
          <Trans
            i18nKey="productDetails:salesData.explainer.footerBringBack"
            components={{
              triggerIcon: (
                <Icon
                  name="circle-question-mark"
                  size="xsmall"
                  iconClassName="inline align-middle text-secondary-500"
                />
              ),
            }}
          />
        </Text>
        <Button
          variant="primary"
          size="small"
          label={t("productDetails:salesData.explainer.dismiss")}
          onClick={onDismiss}
          testId="sales-data-explainer-got-it"
        />
      </div>
    </div>
  );
};
